import axios from 'axios';

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL || '';
const rawPath = process.env.REACT_APP_PRM_API_URL_PATH || '';
const API_BASE_URL = rawPath.startsWith('http://') || rawPath.startsWith('https://')
  ? rawPath
  : 'https://' + rawPath;
export const isLocal = process.env.NODE_ENV === 'development';

/** Auth0 access token via silent refresh (used by AICoachSetup, AuthGate, etc.). */
export async function resolveAccessToken(getAccessTokenSilently) {
  if (typeof getAccessTokenSilently === 'function') {
    return getAccessTokenSilently();
  }
  return null;
}

export const getS3Url = (s3Key) => {
  if (!s3Key) return '';

  if (s3Key.startsWith('https://') || s3Key.startsWith('https://')) {
    return s3Key;
  }

  if (S3_BASE_URL) {
    const cleanKey = s3Key.startsWith('/') ? s3Key.substring(1) : s3Key;
    return `${S3_BASE_URL}/${cleanKey}`;
  }

   
  return s3Key;
};

 
let getAuthTokenCallback = null;
let unauthorizedHandler = null;

export const setAuthTokenGetter = (callback) => {
  getAuthTokenCallback = callback;
};

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

const getAuthToken = async () => {
  if (getAuthTokenCallback) {
    try {
      return await getAuthTokenCallback();
    } catch (error) {
      console.error('Error getting Auth0 token:', error);
      return null;
    }
  }
  return null;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: !isLocal,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PUBLIC_GET_ENDPOINTS = [
  '/admin/user-personas',
  '/admin/templates/formats',
  '/admin/creators',
];

 apiClient.interceptors.request.use(
  async (config) => {
     if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

     const isPublicGetEndpoint = config.method?.toLowerCase() === 'get' &&
      PUBLIC_GET_ENDPOINTS.some(endpoint => config.url?.includes(endpoint));

    if (!isPublicGetEndpoint) {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('API call made without auth token:', config.url);
      }
    }

    if (!isLocal) {
      config.withCredentials = true;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

 apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      if (typeof unauthorizedHandler === 'function') {
        await unauthorizedHandler({ isLocal });
      } else {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

 export const api = {
  storeTokens: (accessToken, refreshToken = '') =>
    apiClient.post('/auth/store-tokens', { accessToken, refreshToken }),
  logout: () => apiClient.post('/auth/logout'),
  getUserInfo: () => apiClient.get('/auth/user-info'),
  googleLogin: (idToken) => apiClient.post('/admin/auth/google-login', { idToken }),
  linkGoogleAccount: (data, token) => apiClient.post('/users/associate-google-account', data, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined
  }),
  verifyToken: () => apiClient.get('/admin/auth/verify'),
  addCredits: (data) => apiClient.post('/admin/credits', data),

   getTemplates: (params) => apiClient.get('/admin/templates', { params }),
  getTemplate: (id) => apiClient.get(`/admin/templates/${id}`),
  createTemplate: (data) => apiClient.post('/admin/templates', data),
  updateTemplate: (id, data) => apiClient.put(`/admin/templates/${id}`, data),
  deleteTemplate: (id) => apiClient.delete(`/admin/templates/${id}`),
  getUploadUrl: (params) => apiClient.get('/admin/templates/upload-url', { params }),
  getCreators: () => apiClient.get('/admin/creators'),
  createCreator: (data) => apiClient.post('/admin/creators', data),
  getCreatorUploadUrl: (params) => apiClient.get('/admin/creators/upload-url', { params }),
  getUserPersonas: () => apiClient.get('/admin/user-personas'),
  createUserPersona: (data) => apiClient.post('/admin/user-personas', data),

  getMimsTypes: () => apiClient.get('/admin/templates/formats'),

  getVendors: ({ keyword, page = 0, perPage = 20, sortField = 'name', sortDirection = 'asc' } = {}) =>
    apiClient.get('/admin/vendors', {
      params: { keyword, page, perPage, sortField, sortDirection },
    }),

  getRoleplayLearners: ({ page = 0, perPage = 50, vendorId } = {}) =>
    apiClient.get('/admin/roleplay-learners', {
      params: { page, perPage, ...(vendorId != null ? { vendorId } : {}) },
    }),
  getRoleplayTags: ({
    page = 0,
    perPage = 100,
    industry,
    skillName,
    name,
    sortField,
    sortDirection,
    vendorId,
  } = {}) =>
    apiClient.get('/admin/roleplay-tags', {
      params: {
        page,
        perPage,
        industry,
        skillName,
        name,
        sortField,
        sortDirection,
        ...(vendorId != null ? { vendorId } : {}),
      },
    }),

  /** Paginated list: response shape `{ success, data: { data: [], page, perPage, totalItems } }` */
  getRoleplayAgents: ({ page = 0, perPage = 50, vendorId } = {}) =>
    apiClient.get('/admin/roleplay-agents', {
      params: { page, perPage, ...(vendorId != null ? { vendorId } : {}) },
    }),
  getRoleplayAgentById: (id) => apiClient.get(`/admin/roleplay-agents/${id}`),
  createRoleplayAgent: (data) => apiClient.post('/admin/roleplay-agents', data),
  updateRoleplayAgent: (id, data) => apiClient.put(`/admin/roleplay-agents/${id}`, data),
  deleteRoleplayAgent: (id) => apiClient.delete(`/admin/roleplay-agents/${id}`),

  getRoleplayScenarios: (params) => apiClient.get('/admin/roleplay-scenarios', { params }),
  getRoleplayScenarioById: (scenarioId) => apiClient.get(`/admin/roleplay-scenarios/${scenarioId}`),
  createRoleplayScenario: (data) => apiClient.post('/admin/roleplay-scenarios', data),
  updateRoleplayScenario: (scenarioId, data) =>
    apiClient.put(`/admin/roleplay-scenarios/${scenarioId}`, data),
  deleteRoleplayScenario: (scenarioId) => apiClient.delete(`/admin/roleplay-scenarios/${scenarioId}`),
  /** Presigned URL for scenario cover image; response includes `imageS3Key` (and `url`, `signedHeaders`). */
  getRoleplayScenarioImageUploadUrl: (params) =>
    apiClient.get('/admin/roleplay-scenarios/image/upload-url', { params }),
  getRoleplayScenarioMediaUploadUrl: (params) =>
    apiClient.get('/admin/roleplay-scenarios/media/upload-url', { params }),
  getRoleplayScenarioAttachmentUploadUrl: (scenarioId, params) =>
    apiClient.get(`/admin/roleplay-scenarios/${scenarioId}/attachments/upload-url`, { params }),
  createRoleplayScenarioAttachment: (scenarioId, data) =>
    apiClient.post(`/admin/roleplay-scenarios/${scenarioId}/attachments`, data),

 
  getRoleplayScenarioAttachmentReadUrl: (scenarioId, params) =>
    apiClient.get(`/admin/roleplay-scenarios/${scenarioId}/attachments/read-url`, { params }),
  getRoleplayScenarioAttachmentDownloadUrl: (scenarioId, params) =>
    apiClient.get(`/admin/roleplay-scenarios/${scenarioId}/attachments/download-url`, { params }),

  /** AI-powered prompt refinements */
  suggestPromptRefinements: (prompt, availablePersonas, availableSkillNames, language) =>
    apiClient.post('/admin/roleplay-scenarios/suggest-prompt-refinements', { prompt, availablePersonas, availableSkillNames, language }),

  /** AI-powered scenario suggestions (5 options with implied mood/pressure) */
  generateScenarioSuggestions: (prompt, selectedTitle, availablePersonas, availableSkillNames, language) =>
    apiClient.post('/admin/roleplay-scenarios/suggest-scenarios', { prompt, selectedTitle, availablePersonas, availableSkillNames, language }),

  /** AI-powered scenario generation from a natural language prompt */
  generateScenarioFromPrompt: (prompt, selectedTitle, availablePersonas, availableSkillNames, language, impliedMood, impliedPressure, impliedDifficulty) =>
    apiClient.post('/admin/roleplay-scenarios/generate', { prompt, selectedTitle, availablePersonas, availableSkillNames, language, impliedMood, impliedPressure, impliedDifficulty }),

  /** AI-powered buyer state prompt generation from scenario context */
  generateBuyerStatePrompt: (data) =>
    apiClient.post('/admin/roleplay-scenarios/buyer-state-prompt/generate', data),

  /** AI-powered behavior prompts (buyerState, communicationTone, replyBehaviour) from scenario context */
  generateBehaviorPrompts: (data) =>
    apiClient.post('/admin/roleplay-scenarios/behavior-prompts/generate', data),

  /** AI-powered objection generation from scenario context */
  generateObjections: (data) =>
    apiClient.post('/admin/roleplay-scenarios/objections/generate', data),

  /** AI-powered conversation prompt generation from scenario context */
  generateConversationPrompts: (data) =>
    apiClient.post('/admin/roleplay-scenarios/conversation-prompts/generate', data),

  /** Tag resource (separate from scenarios) */
  getRoleplayTagById: (tagId) => apiClient.get(`/roleplay-tags/${tagId}`),
  deleteRoleplayTag: (tagId) => apiClient.delete(`/roleplay-tags/${tagId}`),
};

export const uploadToS3 = async (file, fileType) => {
  try {
    const filename = file.name;
    const contentType = file.type;

    const uploadUrlResponse = await api.getUploadUrl({
      filename,
      contentType,
      fileType,
    });

    if (!uploadUrlResponse.data.success) {
      throw new Error('Failed to get upload URL');
    }

    const { url, s3Key, signedHeaders, publicUrl } = uploadUrlResponse.data.data;
    const signedContentType = signedHeaders['content-type']?.[0];

    if (!signedContentType) {
      throw new Error('Content-Type not found in signed headers');
    }

    const uploadResponse = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': signedContentType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`Failed to upload file to S3: ${uploadResponse.status} - ${errorText}`);
    }

 
    return { s3Key, publicUrl: publicUrl || null };
  } catch (err) {
    console.error(`Error uploading ${fileType}:`, err);
    throw err;
  }
};

 export const uploadCreatorPicToS3 = async (file) => {
  try {
    const filename = file.name;
    const contentType = file.type;

    const uploadUrlResponse = await api.getCreatorUploadUrl({
      filename,
      contentType,
    });

    if (!uploadUrlResponse.data.success) {
      throw new Error('Failed to get upload URL');
    }

    const { url, s3Key, signedHeaders } = uploadUrlResponse.data.data;
    const signedContentType = signedHeaders['content-type']?.[0];

    if (!signedContentType) {
      throw new Error('Content-Type not found in signed headers');
    }

    const uploadResponse = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': signedContentType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`Failed to upload file to S3: ${uploadResponse.status} - ${errorText}`);
    }

     return s3Key;
  } catch (err) {
    console.error('Error uploading creator pic:', err);
    throw err;
  }
};

export default apiClient;

